import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  beginSystemCompatibleCheck,
  failSystemCompatibleCheck,
  finishSystemCompatibleCheck,
  SystemCompatibleResultSet
} from 'features/structure/structure-slice';
import axios from 'utils/api';

export type CompatibilityCheckParams = {
  systemUniqueId: string;
  keptComponentUniqueIds: string[];
  // The recommended codes (wants gateway, AHA-6681 sub-build 3): the check
  // is always recommended-bounded now — the sent codes narrow the search and
  // the stored/persisted systemTypes facet is bounded to them (the pnet
  // facet deliberately ignores the type selection, so the narrowing happens
  // here). Empty = nothing recommended: the check records an honest zero
  // without a POST — never a full-universe browse.
  systemTypeCodes: string[];
  // Folded into the dedupe hash but NOT the POST body: a component
  // add/remove changes server-side state without necessarily changing the
  // codes/kept set, so an identical body can legitimately produce new
  // results and must not be deduped away.
  componentsKey: string;
  // The tonnage-filter seed (AHA-6681 tonnage-filtered panel counts):
  // required — not optional — so TypeScript forces every dispatcher to
  // decide, and each resolves it via the shared resolveTonnageSeed on the
  // same Redux inputs, so wrapper/page/derive params can never drift (the
  // thunk dedupes on them). Null = no size filter (dismissed or no
  // effective tonnage); the filters key is then omitted entirely.
  tonnageSeed: { min: number; max: number } | null;
};

type PersistedGroup = {
  totalCount: number;
  systemTypes: { code: string; label: string; count: number }[];
};

// The two-group persisted shape (AHA-6681 dual summary): 'alternatives'
// always, 'kept' only when >=1 component was kept at check time. One
// fetchedAt for the pair — they persist together.
type PersistedLastResults = {
  fetchedAt: string;
  alternatives: PersistedGroup;
  kept?: PersistedGroup;
};

// Persist the summary into the RecommendationSelections JSON (server-side
// JSON_MODIFY merge) so a later session restores it via the strip's context
// GET. Best-effort: the Redux copy already drives this session's display
// and tab signal.
const persistLastResults = (
  systemUniqueId: string,
  lastResults: PersistedLastResults
) => {
  axios
    .patch(
      `existing-equipment/${systemUniqueId}/recommendation-selections/last-results`,
      lastResults
    )
    .catch((error) => {
      console.error('Error persisting compatibility check results:', error);
    });
};

// The result sets in play for a check: the kept-anchored side only exists
// when at least one component is kept. Defined locally rather than reusing
// features/ducted-compatibility's resultSetsFor — the ducted thunks import
// this file, so that import would be a cycle; the decision matches by
// construction.
const resultSetsInPlay = (
  keptComponentUniqueIds: string[]
): SystemCompatibleResultSet[] =>
  keptComponentUniqueIds.length > 0
    ? ['kept', 'alternatives']
    : ['alternatives'];

// Compatible-combinations check for one existing system (AHA-6681 LES-4),
// dispatched by the step's debounced live refetch, the Next click, and the
// Choose Systems missing-summary fallback (sub-build 3). Runs the dual
// kept/alternatives pair when components are kept: the kept-anchored POST
// answers "what pairs with what you're keeping", the unanchored one
// (keptComponentUniqueIds: []) answers "what full replacements exist" —
// both codes-bounded.
// A thunk, NOT a hook or effect: Next unmounts the Equipment Details step
// in the same pass, so completion handlers may only touch Redux dispatch
// and axios — never component state. Never throws and never blocks
// progression; failures leave the previous Redux results under an error
// status. Per-set dedupe + latest-wins stale guard follow the
// ducted-compatibility requestBodyHash pattern.
const runCompatibilityCheck = createAsyncThunk(
  'structure/runCompatibilityCheck',
  async (params: CompatibilityCheckParams, { dispatch, getState }) => {
    const {
      systemUniqueId,
      keptComponentUniqueIds,
      systemTypeCodes,
      componentsKey,
      tonnageSeed
    } = params;

    const resultSets = resultSetsInPlay(keptComponentUniqueIds);

    // activeOnly/inStockOnly mirror the CCB's fresh-open defaults; pageSize 1
    // because only the metadata (totalCount + systemTypes facet) is consumed.
    // The alternatives side is unconstrained by kept equipment by definition
    // (the codes-bounded unanchored search).
    const bodyFor = (resultSet: SystemCompatibleResultSet) => ({
      systemUniqueId,
      keptComponentUniqueIds:
        resultSet === 'alternatives' ? [] : keptComponentUniqueIds,
      systemTypeCodes,
      // The pnet cache universe (AHA-6681 universe scoping): the check is
      // always recommended-bounded, so the sent codes ARE the universe. The
      // honest-zero early return below fires before any POST, so a codes-less
      // check never sends one.
      universeCodes: systemTypeCodes,
      activeOnly: true,
      inStockOnly: true,
      pageNumber: 1,
      pageSize: 1,
      // Wire units are tons; pnet's tonnage range def is nullRangePasses,
      // so unsized combinations survive the filter. A null seed omits the
      // key so the no-tonnage body (and its hash) stays byte-identical to
      // the pre-filter one.
      ...(tonnageSeed
        ? {
            filters: [
              {
                field: 'tonnage',
                kind: 'range',
                min: tonnageSeed.min,
                max: tonnageSeed.max
              }
            ]
          }
        : {})
    });
    const runHashes = {} as Record<SystemCompatibleResultSet, string>;
    resultSets.forEach((resultSet) => {
      runHashes[resultSet] = JSON.stringify({
        ...bodyFor(resultSet),
        componentsKey
      });
    });

    // Per-set dedupe: an identical request already in flight, or already
    // settled with the same params (identical params ⇒ equivalent results —
    // effect re-runs and the Next-click safety net must not re-POST). An
    // error status allows a retry.
    const existing = (getState() as RootState).structure
      .compatibleResultsBySystem[systemUniqueId];
    const setsToFetch = resultSets.filter((resultSet) => {
      const entry = existing?.[resultSet];
      return !(
        entry &&
        entry.requestBodyHash === runHashes[resultSet] &&
        entry.status !== 'error'
      );
    });
    if (setsToFetch.length === 0) return;

    const fetchHashes: Partial<Record<SystemCompatibleResultSet, string>> = {};
    setsToFetch.forEach((resultSet) => {
      fetchHashes[resultSet] = runHashes[resultSet];
    });

    // Reads the post-settle state and PATCHes the combined two-group summary
    // — but only when EVERY in-play set is loaded under this run's hash (a
    // deduped set qualifies: its existing hash equals the run hash). Partial
    // failure or a lost hash race ⇒ no PATCH, the previous persisted summary
    // survives (matching the fail semantics).
    const persistIfRunWon = () => {
      const record = (getState() as RootState).structure
        .compatibleResultsBySystem[systemUniqueId];
      const runWon = resultSets.every((resultSet) => {
        const entry = record?.[resultSet];
        return (
          entry?.status === 'loaded' &&
          entry.requestBodyHash === runHashes[resultSet]
        );
      });
      if (!runWon) return;
      const groupOf = (
        resultSet: SystemCompatibleResultSet
      ): PersistedGroup => {
        const entry = record![resultSet]!;
        return { totalCount: entry.totalCount, systemTypes: entry.systemTypes };
      };
      persistLastResults(systemUniqueId, {
        fetchedAt: new Date().toISOString(),
        alternatives: groupOf('alternatives'),
        ...(resultSets.includes('kept') ? { kept: groupOf('kept') } : {})
      });
    };

    // No recommended codes ⇒ nothing to check (the wants gateway, AHA-6681
    // sub-build 3): skip the POSTs and record the honest zero — a codes-less
    // check must never produce a full-universe summary. begin+finish carry
    // the hashes so the zeros participate in dedupe and the stale guard; all
    // land in the same synchronous frame, so no loading state ever renders.
    if (systemTypeCodes.length === 0) {
      const fetchedAt = new Date().toISOString();
      dispatch(
        beginSystemCompatibleCheck({
          systemUniqueId,
          resultSets,
          hashes: fetchHashes
        })
      );
      setsToFetch.forEach((resultSet) => {
        dispatch(
          finishSystemCompatibleCheck({
            systemUniqueId,
            resultSet,
            requestBodyHash: runHashes[resultSet],
            fetchedAt,
            totalCount: 0,
            systemTypes: []
          })
        );
      });
      persistIfRunWon();
      return;
    }

    // One begin for the run: prunes sets no longer in play and flips only
    // the refetching sets to loading; the POSTs then run in parallel, each
    // settling its own set.
    dispatch(
      beginSystemCompatibleCheck({
        systemUniqueId,
        resultSets,
        hashes: fetchHashes
      })
    );
    const recommendedSet = new Set(systemTypeCodes);
    await Promise.all(
      setsToFetch.map(async (resultSet) => {
        const requestBodyHash = runHashes[resultSet];
        try {
          const response = await axios.post(
            '/existing-equipment/compatible-combinations',
            bodyFor(resultSet)
          );
          const metadata = response.data?.metadata;
          const facetSystemTypes: {
            code: string;
            label: string;
            count: number;
          }[] = metadata?.systemTypes ?? [];
          // Bound the stored/persisted summary to the recommended codes
          // (AHA-6681 requirement B/D — unconditional since sub-build 3):
          // the server's systemTypes facet deliberately ignores the type
          // selection (the CCB pills depend on that), so this store-time
          // filter is what narrows the LES-4 panel, the persisted restore,
          // and the Choose Systems bound. Zero-count entries within the
          // recommended set survive (requirement C). The request codes are
          // recommendation-PRIORITY-ordered, so the survivors are reordered
          // to them: the stored order is what boundedCodesFor and the
          // ducted rail's default panel consume.
          dispatch(
            finishSystemCompatibleCheck({
              systemUniqueId,
              resultSet,
              requestBodyHash,
              fetchedAt: new Date().toISOString(),
              totalCount: metadata?.totalCount ?? 0,
              systemTypes: facetSystemTypes
                .filter((st) => recommendedSet.has(st.code))
                .sort(
                  (a, b) =>
                    systemTypeCodes.indexOf(a.code) -
                    systemTypeCodes.indexOf(b.code)
                )
            })
          );
        } catch (error) {
          dispatch(
            failSystemCompatibleCheck({
              systemUniqueId,
              resultSet,
              requestBodyHash
            })
          );
          console.error('Error checking compatible systems:', error);
        }
      })
    );
    persistIfRunWon();
  }
);

export default runCompatibilityCheck;
